import { DISCORD } from "constants/links";

import React from "react";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Stack, Typography, useTheme, Button, useMediaQuery, alpha, styled, Link } from "@mui/material";
import { unlinkDiscord } from "API/calls";
import Card2 from "components/elements/Card2";
import { Check } from "components/icons";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SubscriptionType } from "types/enums";
import { getColorByMode } from "utils";

import Banner from "./Banner";
import ProBadge from "./ProBadge";

const features = ["f1", "f2", "f3", "f4"];

export const Card2Gradient = styled(Card2)(({ theme }) => ({
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "100%",
    height: "100%",
    background: `radial-gradient(circle at top right, ${alpha("#8A95AF", theme.palette.mode === "dark" ? 0.10 : 0.20)}, transparent 70%)`,
    pointerEvents: "none",
    zIndex: 0,
  },
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

const Discord: React.FC = () => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const { subscriptionType, hasDiscordId, refreshData } = useUserDataContext();

  async function handleUnlinkDiscord() {
    try {
      await unlinkDiscord();
      enqueueSnackbar(t("dashboard.proBenefitsAndCommunity.discord.vip.unlinkSuccess"), { variant: "success" });
      refreshData?.();
    } catch (error: any) {
      enqueueSnackbar(t("dashboard.proBenefitsAndCommunity.discord.vip.unlinkError"), { variant: "error" });
    }
  }

  const handleGoToDiscord = () => {
    window.open(DISCORD, "_blank");
  };

  const getCTA = () => {
    if (subscriptionType !== SubscriptionType.PRO) {
      return (
        <Button variant="contained" onClick={() => navigate("/stosaky#predplatne")}>
          {t("dashboard.proBenefitsAndCommunity.discord.vip.activatePro")}
        </Button>
      );
    }

    if (hasDiscordId) {
      return (
        <Button variant="contained" color="error" onClick={handleUnlinkDiscord} sx={{ backgroundColor: colors.error }}>
          {t("dashboard.proBenefitsAndCommunity.discord.vip.unlink")}
        </Button>
      );
    }

    return (
      <Button variant="contained" onClick={() => (window.location.href = "/oauth2/authorization/discord")}>
        {t("dashboard.proBenefitsAndCommunity.discord.vip.link")}
      </Button>
    );
  };

  return (
    <Stack spacing={3}>
      <Banner />
      <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
        <Typography
          color={getColorByMode(theme, colors.deepMidnightBlue, colors.blueGray)}
          fontSize={{ xs: "0.875rem", sm: "1rem", md: "1.375rem", lg: "1.125rem", xl: "1.375rem" }}
          fontWeight={fontWeight.medium}>
          {t("dashboard.proBenefitsAndCommunity.discord.title")}
        </Typography>
        <Button
          variant="contained"
          size={"small"}
          color={"primary"}
          endIcon={<KeyboardArrowRightIcon />}
          onClick={handleGoToDiscord}>
          {t("dashboard.proBenefitsAndCommunity.discord.cta")}
        </Button>
      </Stack>
      <Typography
        color={colors.blueGray}
        fontSize={{ xs: "0.75rem", md: "1rem" }}
        fontWeight={fontWeight.light}
        textAlign={"left"}>
        <Trans
          i18nKey="dashboard.proBenefitsAndCommunity.discord.description"
          components={{
            aDiscord: <Link href={DISCORD} target="_blank" sx={{ fontWeight: "bold" }} />,
          }}
        />
      </Typography>
      <Card2Gradient borderRadius={8} background={colors.deepMidnightBlue}>
        <Stack spacing={2}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
            <Typography
              color={"white"}
              fontSize={{ xs: "0.875rem", sm: "1rem", md: "1.375rem", lg: "1.125rem", xl: "1.375rem" }}
              fontWeight={fontWeight.medium}>
              {t("dashboard.proBenefitsAndCommunity.discord.vip.title")}
            </Typography>
            <ProBadge size={isMobile ? "small" : "normal"} />
          </Stack>
          <Typography
            color={getColorByMode(theme, colors.gray100, colors.gray700)}
            fontSize={{ xs: "0.75rem", md: "1rem" }}
            fontWeight={fontWeight.light}
            textAlign={"left"}>
            <Trans i18nKey="dashboard.proBenefitsAndCommunity.discord.vip.description" />
          </Typography>
          <Stack direction={"column"} spacing={1.5}>
            {features.map((feature, index) => (
              <Stack key={`${index}-${feature}`} direction="row" spacing={1} alignItems="center">
                <Check sx={{ color: getColorByMode(theme, colors.gray100, colors.gray700), fontSize: 18 }} />
                <Typography
                  fontSize={{ xs: "0.75rem", md: "0.875rem" }}
                  textAlign={"left"}
                  color={getColorByMode(theme, colors.gray100, colors.gray700)}
                  fontWeight={fontWeight.light}>
                  <Trans i18nKey={`dashboard.proBenefitsAndCommunity.discord.vip.${feature}`} />
                </Typography>
              </Stack>
            ))}
          </Stack>
          {getCTA()}
        </Stack>
      </Card2Gradient>
    </Stack>
  );
};

export default Discord;
